import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/admin/layout/AdminLayout';
import { adminService } from '../../../services/admin/adminService';

const EditSubscription = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [paymentStats, setPaymentStats] = useState({});
  
  const [editData, setEditData] = useState({
    package: '',
    startDate: '',
    endDate: '',
    status: 'active'
  });

  // Subscription packages data
  const subscriptionPackages = [
    {
      id: "1",
      name: "Basic",
      description: "Basic subscription package",
      price: 100,
      features: {
        stuff: 2,
        players: 10,
        teams: 2,
        leagues: 0,
        events: 2,
        matches: 1,
        competitions: 0,
      },
      duration: "365",
    },
    {
      id: "2",
      name: "Pro",
      description: "Pro subscription package",
      price: 200,
      features: {
        stuff: 10,
        players: 90,
        teams: 6,
        leagues: 1,
        events: 6,
        matches: 5,
        competitions: 2,
      },
      duration: "365",
    },
    {
      id: "3",
      name: "Enterprise",
      description: "Enterprise subscription package",
      price: 300,
      features: {
        stuff: 30,
        players: 300,
        teams: 15,
        leagues: 12,
        events: -1,
        matches: -1,
        competitions: 10,
      },
      duration: "365",
    },
  ];

  useEffect(() => {
    fetchSubscription();
  }, [subscriptionId]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the dedicated API endpoint to get user subscription
      const response = await adminService.getUserSubscription(subscriptionId);
      const userData = response.data.data;
      
      if (!userData) {
        setError('User not found');
        return;
      }

      // Check if user has a subscription
      if (!userData.subscription) {
        // User exists but has no subscription - allow creating one
        const subscriptionData = {
          userId: userData.user.userId,
          fullName: userData.user.fullName,
          email: userData.user.email,
          userName: userData.user.userName,
          accountType: userData.user.accountType,
          subscription: null
        };

        setSubscription(subscriptionData);
        
        // Set default edit data for new subscription
        setEditData({
          package: '1', // Default to Basic package
          startDate: new Date().toISOString().split('T')[0], // Today
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from today
          status: 'active'
        });

        // Store payment transaction data
        setPaymentTransactions(userData.paymentTransactions || []);
        setPaymentStats(userData.paymentStats || {});
        
        return;
      }

      // Transform the data to match the expected format
      const subscriptionData = {
        userId: userData.user.userId,
        fullName: userData.user.fullName,
        email: userData.user.email,
        userName: userData.user.userName,
        accountType: userData.user.accountType,
        subscription: userData.subscription
      };

      setSubscription(subscriptionData);
      
      // Store payment transaction data
      setPaymentTransactions(userData.paymentTransactions || []);
      setPaymentStats(userData.paymentStats || {});
      
      // Set initial edit data
      const sub = userData.subscription;
      
      console.log('Raw subscription data:', {
        subscription: sub,
        hasStatus: !!sub.status,
        statusValue: sub.status,
        isActiveValue: sub.isActive,
        allKeys: Object.keys(sub)
      });
      
      const startDateFormatted = toInputDate(sub.startDate);
      const endDateFormatted = toInputDate(sub.endDate);
      
      console.log('Setting initial edit data:', {
        originalStartDate: sub.startDate,
        formattedStartDate: startDateFormatted,
        originalEndDate: sub.endDate,
        formattedEndDate: endDateFormatted
      });
      
      // Determine the current status - prioritize status field, fallback to isActive
      let currentStatus = 'inactive';
      if (sub.status) {
        currentStatus = sub.status;
      } else if (sub.isActive !== undefined) {
        currentStatus = sub.isActive ? 'active' : 'inactive';
      }
      
      console.log('Setting edit data with status:', {
        hasStatusField: !!sub.status,
        statusField: sub.status,
        isActive: sub.isActive,
        determinedStatus: currentStatus
      });

      setEditData({
        package: sub.package,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        status: currentStatus
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      if (error.response?.status === 404) {
        setError('User not found');
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else {
        setError(error.response?.data?.message || 'Failed to load subscription details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      
      // Temporary verification that new code is loaded
      console.log('🔄 NEW CODE LOADED - Using status field:', editData.status);
      
      console.log('Saving subscription data:', {
        package: editData.package,
        startDate: editData.startDate,
        endDate: editData.endDate,
        status: editData.status,
        statusType: typeof editData.status,
        editDataObject: editData
      });
      
      // Check if this is a new subscription or existing one
      const isNewSubscription = !subscription.subscription;
      
      if (isNewSubscription) {
        // Create new subscription
        const createPayload = {
          package: editData.package,
          startDate: editData.startDate,
          endDate: editData.endDate,
          status: editData.status
        };
        
        console.log('Sending create payload:', createPayload);
        
        await adminService.updateUserSubscription(subscriptionId, createPayload);
      } else {
        // Update existing subscription
        // Check what has changed
        const packageChanged = editData.package !== subscription.subscription.package;
        const startDateChanged = !datesAreEqual(editData.startDate, subscription.subscription.startDate);
        const endDateChanged = !datesAreEqual(editData.endDate, subscription.subscription.endDate);
        const statusChanged = editData.status !== (subscription.subscription.status || (subscription.subscription.isActive ? 'active' : 'inactive'));
        
        console.log('Changes detected:', {
          packageChanged,
          startDateChanged,
          endDateChanged,
          statusChanged
        });
        
        // If package changed or status changed, use full update
        if (packageChanged || statusChanged) {
          const updatePayload = {
            package: editData.package,
            startDate: editData.startDate,
            endDate: editData.endDate,
            status: editData.status
          };
          
          console.log('Sending update payload:', updatePayload);
          
          await adminService.updateUserSubscription(subscriptionId, updatePayload);
        } else if (startDateChanged || endDateChanged) {
          // Only dates changed, use date update endpoint
          await adminService.updateSubscriptionDates(subscriptionId, {
            startDate: editData.startDate,
            endDate: editData.endDate
          });
        }
      }
      
      // Navigate back to subscriptions list with success message
      navigate('/admin/subscriptions', { 
        state: { 
          message: isNewSubscription ? 'Subscription created successfully!' : 'Subscription updated successfully!',
          type: 'success' 
        }
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      if (error.response?.status === 400) {
        setError(error.response?.data?.message || 'Invalid data provided');
      } else if (error.response?.status === 404) {
        setError('User not found');
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else {
        setError(error.response?.data?.message || 'Failed to update subscription');
      }
    } finally {
      setSaving(false);
    }
  };

  const getPackageInfo = (packageId) => {
    return subscriptionPackages.find(pkg => pkg.id === String(packageId)) || subscriptionPackages[0];
  };

  const formatFeatures = (features) => {
    if (!features) return 'N/A';
    return Object.entries(features)
      .filter(([key, value]) => value !== 0)
      .map(([key, value]) => `${key}: ${value === -1 ? '∞' : value}`)
      .join(', ');
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = new Date(typeof dateValue === 'number' ? dateValue : dateValue);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (input) => {
    // If input is a string, return status badge for that status
    if (typeof input === 'string') {
      const statusStyles = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        expired: 'bg-red-100 text-red-800',
        suspended: 'bg-yellow-100 text-yellow-800',
        pending: 'bg-blue-100 text-blue-800'
      };
      
      const className = statusStyles[input] || statusStyles.inactive;
      return <span className={`px-2 py-1 text-xs font-medium rounded-full ${className}`}>
        {input.charAt(0).toUpperCase() + input.slice(1)}
      </span>;
    }
    
    // Handle subscription data object (existing logic)
    const subscriptionData = input;
    const sub = subscriptionData.subscription;
    
    // First check if there's an explicit status field
    if (sub.status) {
      const statusStyles = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        expired: 'bg-red-100 text-red-800',
        suspended: 'bg-yellow-100 text-yellow-800',
        pending: 'bg-blue-100 text-blue-800'
      };
      
      const className = statusStyles[sub.status] || statusStyles.inactive;
      return <span className={`px-2 py-1 text-xs font-medium rounded-full ${className}`}>
        {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
      </span>;
    }
    
    // Fallback to old logic for backward compatibility
    if (sub.hasPendingPaymentTransaction) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Pending Payment</span>;
    }
    
    if (!sub.isActive) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>;
    }
    
    const endDate = new Date(typeof sub.endDate === 'number' ? sub.endDate : sub.endDate);
    const now = new Date();
    
    if (endDate < now || sub.isExpired) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Expired</span>;
    }
    
    const daysLeft = sub.daysRemaining || Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 30) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">Expiring Soon</span>;
    }
    
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>;
  };

  const selectedPackage = getPackageInfo(editData.package);

  // Helper to compare dates properly
  const datesAreEqual = (date1, date2) => {
    const formatted1 = toInputDate(date1);
    const formatted2 = toInputDate(date2);
    console.log('Comparing dates:', { date1, formatted1, date2, formatted2, equal: formatted1 === formatted2 });
    return formatted1 === formatted2;
  };

  // Helper function to format date for input
  const toInputDate = (dateValue) => {
    console.log('Converting date:', dateValue, 'Type:', typeof dateValue);
    
    if (!dateValue) return '';
    
    let date;
    if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      console.warn('Unknown date format:', dateValue);
      return '';
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateValue);
      return '';
    }
    
    const result = date.toISOString().split('T')[0];
    console.log('Converted to input date:', result);
    return result;
  };



  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error && !subscription) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={() => navigate('/admin/subscriptions')}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
          >
            Back to Subscriptions
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/subscriptions')}
              className="flex items-center text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Subscriptions
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {subscription?.subscription ? 'Edit Subscription' : 'Create Subscription'}
            </h1>
            <p className="text-gray-600 mt-1">
              {subscription?.subscription 
                ? `Update subscription details for ${subscription?.fullName}`
                : `Create new subscription for ${subscription?.fullName}`
              }
            </p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Subscription Details</h2>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6">
                {/* Organization Info (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <div className="flex items-center p-3 bg-gray-50 rounded-md">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 font-medium text-sm">
                          {(subscription.fullName || subscription.userName || 'O').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.fullName}
                      </div>
                      <div className="text-sm text-gray-500">{subscription.email}</div>
                      <div className="text-xs text-gray-400">@{subscription.userName}</div>
                    </div>
                  </div>
                </div>

                {/* Current Status */}
                {subscription?.subscription && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Status
                    </label>
                    <div className="flex items-center space-x-4">
                      {(() => {
                        console.log('🏷️ Current Status Badge - subscription object:', {
                          hasSubscription: !!subscription.subscription,
                          subscriptionStatus: subscription.subscription?.status,
                          subscriptionIsActive: subscription.subscription?.isActive,
                          fullSubscriptionObject: subscription.subscription
                        });
                        return getStatusBadge(subscription);
                      })()}
                      <span className="text-sm text-gray-500">
                        Member since {formatDate(subscription.memberSince)}
                      </span>
                    </div>
                  </div>
                )}

                {/* New Subscription Notice */}
                {!subscription?.subscription && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">New Subscription</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          This user doesn't have an active subscription. You can create one by filling out the form below.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Package Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Package *
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {subscriptionPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`relative rounded-lg border cursor-pointer p-4 ${
                          editData.package === pkg.id
                            ? 'border-orange-500 ring-2 ring-orange-500'
                            : 'border-gray-300'
                        }`}
                        onClick={() => setEditData(prev => ({ ...prev, package: pkg.id }))}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="package"
                            value={pkg.id}
                            checked={editData.package === pkg.id}
                            onChange={(e) => setEditData(prev => ({ ...prev, package: e.target.value }))}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                          />
                          <div className="ml-3">
                            <label className="font-medium text-gray-900 text-sm">
                              {pkg.name}
                            </label>
                            <div className="text-gray-500 text-sm">${pkg.price}/year</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Date Selection */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={editData.startDate}
                      onChange={(e) => setEditData(prev => ({ ...prev, startDate: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={editData.endDate}
                      onChange={(e) => setEditData(prev => ({ ...prev, endDate: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </select>
                  {editData.status && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">Preview: </span>
                      {getStatusBadge(editData.status)}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/subscriptions')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving 
                      ? 'Saving...' 
                      : subscription?.subscription 
                        ? 'Save Changes' 
                        : 'Create Subscription'
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-1">
            {/* Current Subscription Info */}
            {subscription?.subscription && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Current Subscription</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Package</h3>
                      <p className="text-sm text-gray-600">
                        {subscription.subscription.packageName || getPackageInfo(subscription.subscription.package).name}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Dates</h3>
                      <p className="text-sm text-gray-600">
                        Start: {formatDate(subscription.subscription.startDate)}<br/>
                        End: {formatDate(subscription.subscription.endDate)}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Payment Status</h3>
                      <p className="text-sm text-gray-600">
                        {subscription.subscription.paymentTransactionStatus || 'N/A'}
                      </p>
                      {subscription.subscription.paymentTransactionId && (
                        <p className="text-xs text-gray-500 mt-1">
                          Transaction ID: {subscription.subscription.paymentTransactionId}
                        </p>
                      )}
                      {subscription.subscription.hasPendingPaymentTransaction && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending Payment
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Payment Summary */}
                    {Object.keys(paymentStats).length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">Payment Summary</h3>
                        <div className="text-sm text-gray-600 space-y-1">
                          {paymentStats.completed && (
                            <div>Completed: {paymentStats.completed.count} (${paymentStats.completed.totalAmount})</div>
                          )}
                          {paymentStats.pending && (
                            <div>Pending: {paymentStats.pending.count} (${paymentStats.pending.totalAmount})</div>
                          )}
                          {paymentStats.failed && (
                            <div>Failed: {paymentStats.failed.count} (${paymentStats.failed.totalAmount})</div>
                          )}
                        </div>
                      </div>
                    )}

                    {subscription.subscription.daysRemaining && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">Days Remaining</h3>
                        <p className="text-sm text-gray-600">
                          {subscription.subscription.daysRemaining} days
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Package Preview */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {subscription?.subscription ? 'New Package Preview' : 'Package Preview'}
                </h2>
              </div>
              <div className="p-6">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{selectedPackage.name}</h3>
                  <div className="text-3xl font-bold text-orange-600">${selectedPackage.price}</div>
                  <div className="text-sm text-gray-500">per year</div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Features Include:</h4>
                  
                  <div className="space-y-2">
                    {Object.entries(selectedPackage.features).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 capitalize">{key}:</span>
                        <span className="font-medium text-gray-900">
                          {value === -1 ? 'Unlimited' : value === 0 ? 'Not included' : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Transactions Section */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Payment History</h2>
              </div>
              <div className="p-6">
                {/* Payment Statistics */}
                {Object.keys(paymentStats).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Payment Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(paymentStats).map(([status, stats]) => (
                        <div key={status} className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {status}
                          </div>
                          <div className="mt-1">
                            <div className="text-lg font-semibold text-gray-900">{stats.count}</div>
                            <div className="text-sm text-gray-600">${stats.totalAmount}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Transactions */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Recent Transactions</h3>
                  {paymentTransactions.length > 0 ? (
                    <div className="space-y-3">
                      {paymentTransactions.slice(0, 5).map((transaction) => (
                        <div key={transaction._id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                  ${transaction.amount}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  transaction.status === 'refunded' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {transaction.status}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                ID: {transaction.transactionId}
                              </div>
                              {transaction.reason && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {transaction.reason}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                          
                          {/* Additional transaction details */}
                          {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs text-gray-500">
                                <strong>Metadata:</strong>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {transaction.metadata.packageId && (
                                  <div>Package: {transaction.metadata.packageId}</div>
                                )}
                                {transaction.metadata.paymentIntentId && (
                                  <div>Payment Intent: {transaction.metadata.paymentIntentId}</div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Payment dates */}
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                              {transaction.paidAt && (
                                <div>
                                  <strong>Paid:</strong> {new Date(transaction.paidAt).toLocaleDateString()}
                                </div>
                              )}
                              {transaction.expiresAt && (
                                <div>
                                  <strong>Expires:</strong> {new Date(transaction.expiresAt).toLocaleDateString()}
                                </div>
                              )}
                              {transaction.refundedAt && (
                                <div>
                                  <strong>Refunded:</strong> {new Date(transaction.refundedAt).toLocaleDateString()}
                                </div>
                              )}
                              {transaction.cancelledAt && (
                                <div>
                                  <strong>Cancelled:</strong> {new Date(transaction.cancelledAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {paymentTransactions.length > 5 && (
                        <div className="text-center">
                          <div className="text-sm text-gray-500">
                            Showing 5 of {paymentTransactions.length} transactions
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-gray-500 text-sm">No payment transactions found</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EditSubscription;
